/**
 * @fileoverview Configured Axios client instance with authentication and error handling
 *
 * Supports two authentication mechanisms:
 * 1. Internal session token (from auth store after Okta sync) - Main app, uses Authorization: Bearer {token}
 * 2. Share session tokens (sessionStorage) - Share portal, uses X-Share-Session: {token}
 *
 * Authentication flow:
 * 1. User authenticates with Okta
 * 2. Frontend sends Okta token to /auth/sync
 * 3. Backend validates Okta token and returns internal session token
 * 4. All subsequent API calls use the internal session token
 *
 * Features:
 * - Token retrieval from auth store (internal session token)
 * - Automatic retry with exponential backoff for network/5xx errors
 * - Max 3 retries with increasing delays (1s, 2s, 4s)
 * - No retry for client errors (4xx)
 * - Automatic 401 handling with token refresh
 */

import axios from 'axios'
import { isRetryableError } from '@/utils/errorHandling'

/**
 * @typedef {Object} ApiError
 * @property {string} message - Error message
 * @property {number} [status] - HTTP status code
 * @property {Object} [data] - Additional error data
 */

/**
 * Retry configuration
 */
const MAX_RETRIES = 3
const RETRY_DELAY_BASE = 1000 // 1 second

/**
 * Token getter function - will be set by auth store
 * @type {(() => Promise<string|null>)|null}
 */
let tokenGetter = null

/**
 * Session expiration handler - set by auth store
 * @type {((context?: { reason: string, status?: number, url?: string }) => Promise<void>|void)|null}
 */
let sessionExpiredHandler = null

/**
 * Normalize API base URL so requests always target /api.
 *
 * Accepts values like:
 * - /api
 * - http://localhost:8000
 * - http://localhost:8000/api
 *
 * @param {string|undefined} rawBaseUrl
 * @returns {string}
 */
export function resolveApiBaseUrl(rawBaseUrl) {
  const base = rawBaseUrl || '/api'
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base

  if (trimmed === '/api') {
    return '/api'
  }

  if (trimmed.endsWith('/api')) {
    return trimmed
  }

  return `${trimmed}/api`
}

/**
 * Set the token getter function
 * Called by the auth store during initialization
 *
 * @param {() => Promise<string|null>} getter - Function that returns the current access token
 */
export function setTokenGetter(getter) {
  tokenGetter = getter
}

/**
 * Set session expiration handler
 * Called by auth store to handle unrecoverable authentication failures.
 *
 * @param {(context?: { reason: string, status?: number, url?: string }) => Promise<void>|void} handler
 */
export function setSessionExpiredHandler(handler) {
  sessionExpiredHandler = handler
}

/**
 * Base API client instance with authentication and error handling
 */
const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

/**
 * Request interceptor to add authentication token and initialize retry count
 */
apiClient.interceptors.request.use(
  async config => {
    console.debug(`[API Client] Request interceptor: ${config.method?.toUpperCase()} ${config.url}`)

    // Get access token from auth store if available
    if (tokenGetter) {
      try {
        console.debug('[API Client] Request interceptor: Getting token from tokenGetter...')
        const token = await tokenGetter()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          // Log token for debugging (first 30 chars only for security)
          const tokenPrefix = token.substring(0, 30)
          console.debug(
            `[API Client] Request interceptor: Token added to ${config.url} (first 30 chars): ${tokenPrefix}...`
          )
        } else {
          console.warn(`[API Client] Request interceptor: No token available for ${config.url}`)
        }
      } catch (error) {
        console.error('[API Client] Request interceptor: Failed to get access token:', error)
        // Continue without token - let backend handle unauthorized request
      }
    } else {
      console.warn('[API Client] Request interceptor: No tokenGetter registered')
    }

    // Initialize retry count if not present
    if (!config.retryCount) {
      config.retryCount = 0
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

/**
 * Response interceptor to handle errors, retries, and token refresh
 */
apiClient.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config

    // Handle 401 Unauthorized errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      console.debug('[API Client] 401 error detected, attempting token refresh...')

      try {
        // Dynamically import auth store to avoid circular dependency
        const { useAuthStore } = await import('@/stores/auth')
        const authStore = useAuthStore()

        // Attempt to refresh the session token
        const refreshed = await authStore.refreshSessionToken()

        if (refreshed) {
          console.debug('[API Client] Token refresh successful, retrying original request')

          // Get the new token and retry the original request
          const newToken = await authStore.getAccessToken()
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return apiClient(originalRequest)
          }
        }

        console.warn('[API Client] Token refresh failed, authentication required')
      } catch (refreshError) {
        console.error('[API Client] Error during token refresh:', refreshError)
      }

      // If refresh failed, trigger global session-expired handling immediately.
      // This avoids leaving users on a stale "authenticated" screen until navigation.
      if (sessionExpiredHandler) {
        try {
          await sessionExpiredHandler({
            reason: 'refresh_failed',
            status: 401,
            url: originalRequest?.url
          })
        } catch (handlerError) {
          console.error('[API Client] Session expired handler failed:', handlerError)
        }
      }

      // Only log in development or for non-auth endpoints to reduce noise
      if (import.meta.env.DEV && !originalRequest.url?.includes('/auth/')) {
        console.warn('Authentication token expired and refresh failed - re-authentication required')
      }

      // Don't redirect here - let the router navigation guard handle it
      // The Okta SDK's navigationGuard will trigger signInWithRedirect
      // when the user navigates to a requiresAuth route
      return Promise.reject(createNormalizedError(error))
    }

    // Retry logic for network errors and 5xx server errors
    if (isRetryableError(error) && originalRequest.retryCount < MAX_RETRIES) {
      originalRequest.retryCount += 1

      // Calculate exponential backoff delay: 1s, 2s, 4s
      const delay = RETRY_DELAY_BASE * Math.pow(2, originalRequest.retryCount - 1)

      console.debug(
        `Retrying request (attempt ${originalRequest.retryCount}/${MAX_RETRIES}) after ${delay}ms`,
        originalRequest.url
      )

      // Wait for the delay
      await new Promise(resolve => setTimeout(resolve, delay))

      // Retry the request
      return apiClient(originalRequest)
    }

    // Normalize all other errors
    return Promise.reject(createNormalizedError(error))
  }
)

/**
 * Creates a normalized error object from various error types
 *
 * @param {Error | Object} error - The original error
 * @returns {ApiError} Normalized error object
 */
function createNormalizedError(error) {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.detail || error.response.data?.message || error.message,
      status: error.response.status,
      data: error.response.data
    }
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'No response from server. Please check your connection.',
      status: 0,
      data: null
    }
  } else {
    // Error setting up the request
    return {
      message: error.message || 'An unexpected error occurred',
      status: 0,
      data: null
    }
  }
}

/**
 * Share Session Token Management
 * Used by share portal for external user authentication
 */

/**
 * Store share session token and ID in sessionStorage
 *
 * @param {string} token - Share session token
 * @param {string} shareId - Share UUID
 * @example
 * setShareSessionToken(authResponse.session_token, shareId)
 */
export function setShareSessionToken(token, shareId) {
  sessionStorage.setItem('share_session_token', token)
  if (shareId) {
    sessionStorage.setItem('share_session_id', shareId)
  }
}

/**
 * Get share session token from sessionStorage
 *
 * @returns {string|null} Share session token or null if not found
 * @example
 * const token = getShareSessionToken()
 */
export function getShareSessionToken() {
  return sessionStorage.getItem('share_session_token')
}

/**
 * Clear share session token and ID from sessionStorage
 *
 * @example
 * clearShareSessionToken()
 */
export function clearShareSessionToken() {
  sessionStorage.removeItem('share_session_token')
  sessionStorage.removeItem('share_session_id')
}

export default apiClient
